// MCP Registry — cards for every connected MCP, filterable.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { Card } from '@/components/primitives/Card';
import { Badge } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader } from '@/components/PageHeader';
import { listMcpConnections } from '@/lib/mcps-api';
import { humanizeEnum } from '@/lib/format';

const CATEGORIES = ['ALL', 'DMS', 'OCR', 'CRM', 'ERP', 'CALENDAR', 'WORKFLOW', 'HR', 'RECRUITING', 'CUSTOM'] as const;

export function McpsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('ALL');
  const [status, setStatus] = useState<'ALL' | 'Connected' | 'Warning' | 'Available' | 'Missing'>('ALL');

  const query = useQuery({ queryKey: ['mcps'], queryFn: listMcpConnections });

  const visible = useMemo(() => {
    if (!query.data) return [];
    return query.data.filter((m) => {
      if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (category !== 'ALL' && m.category !== category) return false;
      if (status !== 'ALL' && m.status !== status) return false;
      return true;
    });
  }, [query.data, search, category, status]);

  return (
    <PageContainer>
      <PageHeader
        title="MCP Tools"
        description="External systems Qualibot agents call through the MCP gateway"
        actions={
          <Btn kind="primary" icon={Plus} onClick={() => navigate('/mcp-tools/new')}>
            Add MCP
          </Btn>
        }
      />

      <div className="flex items-center gap-2.5 mb-5">
        <div className="relative w-[280px]">
          <Search size={14} className="absolute left-3 top-[10px] text-text-3 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-8 pr-3 rounded-[10px] border border-border-strong bg-surface text-[13px] outline-none focus:border-accent"
            placeholder="Search MCPs…"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as typeof category)}
          className="h-9 w-[150px] rounded-[10px] border border-border-strong bg-surface px-3 text-[13px]"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c === 'ALL' ? 'Category: All' : humanizeEnum(c)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="h-9 w-[150px] rounded-[10px] border border-border-strong bg-surface px-3 text-[13px]"
        >
          <option value="ALL">Status: All</option>
          <option value="Connected">Connected</option>
          <option value="Warning">Warning</option>
          <option value="Available">Available</option>
          <option value="Missing">Missing</option>
        </select>
      </div>

      {query.isLoading ? (
        <div className="text-[13px] text-text-3 py-10 text-center">Loading MCPs…</div>
      ) : visible.length === 0 ? (
        <Card>
          <Empty
            title="No MCPs match"
            body="Adjust your filters or connect a new one."
            actions={
              <Btn kind="primary" icon={Plus} onClick={() => navigate('/mcp-tools/new')}>
                Add MCP
              </Btn>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-3.5">
          {visible.map((m) => (
            <Card key={m.id} pad={0} onClick={() => navigate(`/mcp-tools/${m.id}`)}>
              <div className="p-[18px]">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold text-[14.5px]">{m.name}</span>
                  <StatusBadge status={m.status} />
                </div>
                <div className="text-[12.5px] text-text-3 mb-1">{humanizeEnum(m.category)}</div>
                <div className="text-[13px] text-text-2 min-h-[36px]">{m.description}</div>
                <div className="flex items-center flex-wrap gap-1.5 mt-3">
                  <Badge>{m.toolCount} tools</Badge>
                  {m.uptime && <Badge>Uptime {m.uptime}</Badge>}
                  {m.isMock && <Badge tone="info">Mock</Badge>}
                </div>
              </div>
              <div className="border-t border-border px-[18px] py-2.5 text-[12px] text-text-3">
                Used by:{' '}
                {m.usedByAgents.length > 0 ? m.usedByAgents.join(', ') : 'no agents yet'}
              </div>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
